import * as React from 'react';

import {
  Button,
  cn,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { LucideArrowRight } from 'lucide-react';

import * as state from '../../_state/containerDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import {
  ContainerRecord,
  ContainerStatus,
  ContainerTransitionAction,
  enumContainerStatus,
  enumContainerTransitionAction,
} from '../../types/container';

import { useTranslations as useSharedTranslations } from '~/shared/i18n/useTranslations';

interface ContainerTransitionDialogProps {
  container: ContainerRecord;
  onSuccess: () => void;
  onCancel: () => void;
}

type TransitionConfig = {
  action: ContainerTransitionAction;
  nextStatus: ContainerStatus;
  requiresContainerNumber?: boolean;
  requiresSealNumber?: boolean;
  requiresFacilityId?: boolean;
  requiresLoadId?: boolean;
};

export const TRANSITION_MAP: Partial<
  Record<ContainerStatus, TransitionConfig>
> = {
  [enumContainerStatus.AtPort]: {
    action: enumContainerTransitionAction.Pickup,
    nextStatus: enumContainerStatus.InTransitToFacility,
    requiresContainerNumber: true,
  },
  [enumContainerStatus.InTransitToFacility]: {
    action: enumContainerTransitionAction.ArriveAtFacility,
    nextStatus: enumContainerStatus.AtFacilityEmpty,
    requiresContainerNumber: true,
  },
  [enumContainerStatus.AtFacilityEmpty]: {
    action: enumContainerTransitionAction.Load,
    nextStatus: enumContainerStatus.AtFacilityLoaded,
    requiresSealNumber: true,
    requiresLoadId: true,
  },
  [enumContainerStatus.AtFacilityLoaded]: {
    action: enumContainerTransitionAction.DepartFacility,
    nextStatus: enumContainerStatus.InTransitToPort,
  },
  [enumContainerStatus.InTransitToPort]: {
    action: enumContainerTransitionAction.ReturnToPort,
    nextStatus: enumContainerStatus.AtPortReadyToShip,
  },
  [enumContainerStatus.AtPortReadyToShip]: {
    action: enumContainerTransitionAction.Ship,
    nextStatus: enumContainerStatus.Shipped,
  },
};

//
// ⚛️ ⚛️ ⚛️ CONTAINER TRANSITION DIALOG ⚛️ ⚛️ ⚛️
//
export const ContainerTransitionDialog: React.FC<
  ContainerTransitionDialogProps
> = ({ container, onSuccess, onCancel }) => {
  // Translations
  const t = useTranslations();
  const sharedT = useSharedTranslations();
  const toast = useToast();

  // States
  const [isLoading, setIsLoading] = React.useState(false);
  const [containerNumber, setContainerNumber] = React.useState(
    container.containerNumber ?? '',
  );
  const [sealNumber, setSealNumber] = React.useState(
    container.sealNumber ?? '',
  );

  // Atoms
  const pickupContainer = useSetAtom(state.$pickupContainer);
  const arriveAtFacility = useSetAtom(state.$arriveContainerAtFacility);
  const loadContainer = useSetAtom(state.$loadContainer);
  const departFacility = useSetAtom(state.$departContainerFacility);
  const returnToPort = useSetAtom(state.$returnContainerToPort);
  const shipContainer = useSetAtom(state.$shipContainer);

  const statusLabels: Record<ContainerStatus, string> = {
    [enumContainerStatus.AtPort]: sharedT.containerStatus.atPort,
    [enumContainerStatus.InTransitToFacility]:
      sharedT.containerStatus.inTransitToFacility,
    [enumContainerStatus.AtFacilityEmpty]:
      sharedT.containerStatus.atFacilityEmpty,
    [enumContainerStatus.AtFacilityLoaded]:
      sharedT.containerStatus.atFacilityLoaded,
    [enumContainerStatus.InTransitToPort]:
      sharedT.containerStatus.inTransitToPort,
    [enumContainerStatus.AtPortReadyToShip]:
      sharedT.containerStatus.atPortReadyToShip,
    [enumContainerStatus.Shipped]: sharedT.containerStatus.shipped,
    [enumContainerStatus.Voided]: sharedT.containerStatus.voided,
  };

  const transition = TRANSITION_MAP[container.status];

  if (!transition) {
    return null;
  }

  const title = t.containersView.transitionDialog.title;
  const confirmLabel =
    t.containersView.transitionDialog.actions[transition.action];
  const cancelLabel = t.containersView.transitionDialog.cancel;

  const handleTransition = async () => {
    setIsLoading(true);
    try {
      switch (transition.action) {
        case enumContainerTransitionAction.Pickup:
          await pickupContainer({
            id: container.id,
            containerNumber: containerNumber || undefined,
          });
          break;
        case enumContainerTransitionAction.ArriveAtFacility:
          await arriveAtFacility({
            id: container.id,
            facilityId: container.facilityId ?? '',
            containerNumber: containerNumber || undefined,
          });
          break;
        case enumContainerTransitionAction.Load:
          await loadContainer({
            id: container.id,
            loadId: container.loadId ?? '',
            sealNumber: sealNumber || undefined,
          });
          break;
        case enumContainerTransitionAction.DepartFacility:
          await departFacility({ id: container.id });
          break;
        case enumContainerTransitionAction.ReturnToPort:
          await returnToPort({ id: container.id });
          break;
        case enumContainerTransitionAction.Ship:
          await shipContainer({ id: container.id });
          break;
      }

      toast.success(
        t.containersView.messages.transitionSuccess(
          statusLabels[transition.nextStatus],
        ),
      );
      onSuccess();
    } catch (err) {
      toast.error(
        t.containersView.transitionDialog.transitionError(
          err instanceof Error ? err.message : t.common.unknown,
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogOverlay
      isOpen
      className={cn('@ContainerTransitionDialog')}
      onOpenChange={onCancel}
    >
      <DialogContent>
        <DialogHeader>
          <div className="text-base font-semibold">{title}</div>
        </DialogHeader>
        <div className="flex flex-col gap-4 px-4">
          <p className="text-sm text-neutral/60">
            {t.containersView.transitionDialog.message(
              statusLabels[container.status],
              statusLabels[transition.nextStatus],
            )}
          </p>

          {transition.requiresContainerNumber && (
            <TextField
              label={t.containersView.transitionDialog.fields.containerNumber}
              value={containerNumber}
              onChange={setContainerNumber}
              placeholder={
                t.containersView.transitionDialog.placeholders.containerNumber
              }
            />
          )}

          {transition.requiresSealNumber && (
            <TextField
              label={t.containersView.transitionDialog.fields.sealNumber}
              value={sealNumber}
              onChange={setSealNumber}
              placeholder={
                t.containersView.transitionDialog.placeholders.sealNumber
              }
            />
          )}

          <div className="flex justify-end gap-2 py-4">
            <Button
              variant="text"
              color="neutral"
              onPress={onCancel}
              isDisabled={isLoading}
            >
              {cancelLabel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleTransition}
              isDisabled={isLoading}
              icon={<LucideArrowRight className="size-4" />}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
